import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge
} from 'reactstrap';
import { CheckIcon, XMarkIcon, XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

import { fetchProduksWithDetail } from '../../../services/produkService';
import { getSnapToken } from '../../../services/langgananService';

// --- Komponen PricingCard ---
const PricingCard = ({ product, onBuyClick, isLoading, isAlreadySubscribed }) => {
  const getDurationText = (days) => {
    if (days === 6) return "6 Bulan";
    if (days === 12) return "1 Tahun";
    if (days === 9999) return "Selamanya";
    return `${days} Hari`;
  };
  const durationText = getDurationText(product.durasi_hari);

  const normalizedRibbon = product.ribbon ? product.ribbon.trim().toUpperCase() : '';
  const isPopular = normalizedRibbon === 'PALING DIMINATI' || normalizedRibbon === 'PALING LAKU' || normalizedRibbon === 'PALING';
  const isBestChoice = normalizedRibbon === 'PILIHAN TERBAIK' || normalizedRibbon === 'PILIHAN';

  return (
    <Card className={`pricing-card-custom shadow-sm h-100 position-relative ${isBestChoice || isPopular ? 'border-primary border-2' : 'border'}`}>
      {product.ribbon && (
        <div className="position-absolute top-0 end-0" style={{ zIndex: 10 }}>
          <Badge color={isPopular ? 'warning' : 'success'} className="p-2 fs-6 rounded-bottom-start rounded-end-0 text-white">
            {product.ribbon}
          </Badge>
        </div>
      )}
      <CardBody className="p-3 d-flex flex-column">
        <h3 className="h5 fw-bold text-dark mb-2">{product.nama_produk}</h3>
        <div className="my-2">
          {product.harga_coret && product.harga_coret > product.harga && (
            <p className="text-muted text-decoration-line-through mb-0 small">
              Rp. {product.harga_coret.toLocaleString('id-ID')}
            </p>
          )}
          <p className="h4 fw-bold text-primary mb-0">
            Rp. {product.harga.toLocaleString('id-ID')}
          </p>
        </div>
        <p className="text-muted mb-3 small">/ {durationText}</p>
        <ul className="list-unstyled flex-grow-1 mb-3 pricing-features-list">
          {product.items?.map((item, index) => (
            <li key={index} className="d-flex align-items-start mb-1 small">
              {item.available === "1" ? (
                <div className="icon-feature-wrapper-active me-2 flex-shrink-0">
                  <CheckIcon className="icon-feature-check" />
                </div>
              ) : (
                <div className="icon-feature-wrapper-inactive me-2 flex-shrink-0">
                  <XMarkIcon className="icon-feature-xmark" />
                </div>
              )}
              <span className={`text-dark ${item.available === "0" ? 'opacity-50' : ''}`}>
                {item.detail_name}
              </span>
            </li>
          ))}
        </ul>
        <Button
          color="primary"
          className="w-100 py-2 fw-bold mt-auto small"
          onClick={() => onBuyClick(product.id_produk)}
          disabled={isLoading || isAlreadySubscribed}
        >
          {isLoading ? <Spinner size="sm" /> : isAlreadySubscribed ? 'Sudah Berlangganan' : 'Beli Paket'}
        </Button>
      </CardBody>
    </Card>
  );
};

// --- Komponen Skeleton Card ---
const SkeletonCard = () => (
  <Card className="pricing-card-custom shadow-sm h-100 position-relative">
    <CardBody className="p-3 d-flex flex-column">
      <div className="skeleton-loader-container">
        <div className="skeleton-line skeleton-title mb-2"></div>
        <div className="skeleton-line skeleton-price mb-3"></div>
        <div className="skeleton-line skeleton-text mb-3"></div>
        <ul className="list-unstyled flex-grow-1 mb-3">
          <li className="skeleton-line skeleton-feature mb-1"></li>
          <li className="skeleton-line skeleton-feature mb-1"></li>
          <li className="skeleton-line skeleton-feature mb-1"></li>
          <li className="skeleton-line skeleton-feature mb-1"></li>
        </ul>
      </div>
      <Button color="secondary" className="w-100 py-2 fw-bold mt-auto small" disabled>
        Memuat...
      </Button>
    </CardBody>
  </Card>
);

// --- Komponen PaketLangganan ---
const PaketLangganan = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentLoadingId, setPaymentLoadingId] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [showRedirectPopup, setShowRedirectPopup] = useState(false);
  const [paymentRedirectUrl, setPaymentRedirectUrl] = useState('');
  const [showPendingPopup, setShowPendingPopup] = useState(false);
  const [pendingPaymentUrl, setPendingPaymentUrl] = useState('');
  const [pendingOrderId, setPendingOrderId] = useState('');
  const [pendingMessage, setPendingMessage] = useState('');
  const [showSettlementPopup, setShowSettlementPopup] = useState(false);
  const [showExpiredPopup, setShowExpiredPopup] = useState(false);
  const [expiredMessage, setExpiredMessage] = useState('');
  
  const [userAccessLevel, setUserAccessLevel] = useState(null); 

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const storedUser = JSON.parse(localStorage.getItem('user'));
        const fetchedAccessLevel = storedUser?.access_level || 1; 
        setUserAccessLevel(fetchedAccessLevel);

        const searchParams = new URLSearchParams(location.search);
        const filters = {
          id_tipe_konten: searchParams.get('id_tipe_konten') || '2',
          id_jenis_seleksi: searchParams.get('id_jenis_seleksi') || '1',
          jenis_paket: searchParams.get('kategori_paket') 
        };
        const response = await fetchProduksWithDetail(filters);
        if (isMounted) {
          if (Array.isArray(response.data)) {
            const transformedProducts = response.data.map(product => ({
              ...product,
              items: product.items || [],
              harga: typeof product.harga === 'string' ? parseInt(product.harga) : product.harga,
              harga_coret: product.harga_coret
                ? (typeof product.harga_coret === 'string' ? parseInt(product.harga_coret) : product.harga_coret)
                : null,
            }));
            const sortedProducts = transformedProducts.sort((a, b) => a.id_produk - b.id_produk);
            setProducts(sortedProducts);
          } else {
            setProducts([]);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Gagal memuat data produk');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, [location.search]);

  const handleBeliPaketClick = async (idProduk) => {
    try {
      setPaymentLoadingId(idProduk);
      setPaymentError(null);
      const parsedIdProduk = parseInt(idProduk, 10);
      const response = await getSnapToken(parsedIdProduk);

      if (response.success && response.data) {
        const { status, redirect_url, order_id, message } = response.data;
        
        if (status === 'settlement') {
          setShowSettlementPopup(true);
        } else if (status === 'PENDING') {
          setPendingPaymentUrl(redirect_url);
          setPendingOrderId(order_id);
          setPendingMessage(message);
          setShowPendingPopup(true);
        } else if (status === 'EXPIRED') {
          setExpiredMessage(message);
          setShowExpiredPopup(true);
        } else if (redirect_url) {
          setPaymentRedirectUrl(redirect_url);
          setShowRedirectPopup(true);
        } else {
          setPaymentError(response.message || 'Gagal mendapatkan token pembayaran.');
        }
      } else {
        setPaymentError(response.message || 'Gagal mendapatkan token pembayaran.');
      }
    } catch (err) {
      console.error(err);
      setPaymentError('Terjadi kesalahan saat memproses pembayaran. Silakan coba lagi.');
    } finally {
      setPaymentLoadingId(null);
    }
  };

  if (userAccessLevel === null) {
      return <div className="text-center py-5">Memuat data pengguna...</div>;
  }

  // Mendapatkan produk berdasarkan indeks untuk logika yang Anda berikan
  const product0 = products[0];
  const product1 = products[1];
  const product2 = products[2];

  // Menentukan status berlangganan berdasarkan logika
  const checkAccessLevels = (productAccessLevel) => {
    // KONDISI 1: Jika index 2 tidak null
    if (product2 && product2.access_level !== null) {
      return productAccessLevel >= 0 && productAccessLevel <= 3;
    }
    // KONDISI 2: Jika index 2 null DAN index 1 tidak null
    else if ((!product2 || product2.access_level === null) && product1 && product1.access_level !== null) {
      return productAccessLevel >= 0 && productAccessLevel <= 1;
    }
    // KONDISI 3: Jika index 0 tidak null DAN index 1 & 2 null
    else if (product0 && product0.access_level !== null && (!product1 || product1.access_level === null) && (!product2 || product2.access_level === null)) {
      return productAccessLevel === 0;
    }
    // Kondisi default: tidak ada yang berlangganan
    return false;
  };

  return (
    <div className="bg-light min-vh-100 pb-5">
      <Container fluid className="py-5 px-4 custom-container-padding">
        {paymentError && (
          <Alert color="danger" className="text-center mb-4">
            <XCircleIcon style={{ width: '1.5rem', height: '1.5rem', display: 'inline-block' }} className="me-2" />
            {paymentError}
          </Alert>
        )}
        
        {loading ? (
          <Row className="g-4 justify-content-left">
            {Array.from({ length: 3 }).map((_, index) => (
              <Col key={index} xl="4" lg="4" md="6" sm="12">
                <SkeletonCard />
              </Col>
            ))}
          </Row>
        ) : error ? (
          <div className="text-center py-4">
            <div className="text-danger mb-3">
              <XCircleIcon className="mx-auto" style={{ width: '3rem', height: '3rem' }} />
            </div>
            <p className="text-danger fw-medium">Gagal memuat data produk</p>
            <p className="text-muted">{error}</p>
            <Button onClick={() => window.location.reload()} color="primary" className="mt-3">
              Coba Lagi
            </Button>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">Tidak ada produk yang tersedia</p>
          </div>
        ) : (
          <Row className="g-4 justify-content-left">
            {products.map((product, index) => {
              // Menentukan apakah produk sudah berlangganan berdasarkan logika baru
              const isAlreadySubscribed = checkAccessLevels(index);

              return (
                <Col key={product.id_produk} xl="4" lg="4" md="6" sm="12">
                  <PricingCard
                    product={product}
                    onBuyClick={handleBeliPaketClick}
                    isLoading={paymentLoadingId === product.id_produk}
                    isAlreadySubscribed={isAlreadySubscribed}
                  />
                </Col>
              );
            })}
          </Row>
        )}
        
        <div className="mt-5 text-center text-muted small">
          *Harga dapat berubah sewaktu-waktu tanpa pemberitahuan sebelumnya
        </div>
      </Container>

      <Modal isOpen={showPendingPopup} toggle={() => setShowPendingPopup(false)} centered>
        <ModalHeader toggle={() => setShowPendingPopup(false)}>
          Transaksi Belum Selesai
        </ModalHeader>
        <ModalBody className="text-center">
          <p>{pendingMessage}</p>
          <p className="fw-bold">Silakan selesaikan pembayaran untuk transaksi sebelumnya </p>
          <p className="text-muted small">Order ID: {pendingOrderId}</p>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button color="secondary" onClick={() => setShowPendingPopup(false)}>Tutup</Button>
          <Button color="primary" onClick={() => { window.location.href = pendingPaymentUrl; }}>Lanjutkan Pembayaran</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showSettlementPopup} toggle={() => setShowSettlementPopup(false)} centered>
        <ModalHeader toggle={() => setShowSettlementPopup(false)}>
          <span className="text-success">Transaksi Berhasil</span>
        </ModalHeader>
        <ModalBody className="text-center">
          <CheckCircleIcon className="text-success mb-3" style={{ width: '3rem', height: '3rem' }} />
          <p className="fw-bold">Anda sudah berhasil berlangganan paket ini.</p>
          <p className="text-muted">Tidak bisa melakukan transaksi baru untuk paket yang sama.</p>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button color="success" onClick={() => setShowSettlementPopup(false)}>Oke</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showRedirectPopup} toggle={() => setShowRedirectPopup(false)} centered>
        <ModalHeader toggle={() => setShowRedirectPopup(false)}>Lanjutkan Pembayaran</ModalHeader>
        <ModalBody className="text-center">
          <p>Kamu akan diarahkan ke halaman pembayaran Midtrans.</p>
          <p className="fw-bold">Pastikan kamu tidak menutup tab ini sampai pembayaran selesai.</p>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button color="secondary" onClick={() => setShowRedirectPopup(false)}>Batalkan</Button>
          <Button color="primary" onClick={() => { window.location.href = paymentRedirectUrl; }}>Lanjutkan</Button>
        </ModalFooter>
      </Modal>
      
      <Modal isOpen={showExpiredPopup} toggle={() => setShowExpiredPopup(false)} centered>
        <ModalHeader toggle={() => setShowExpiredPopup(false)}>
          <span className="text-warning">Transaksi Kedaluwarsa</span>
        </ModalHeader>
        <ModalBody className="text-center">
          <XCircleIcon className="text-warning mb-3" style={{ width: '3rem', height: '3rem' }} />
          <p className="fw-bold">{expiredMessage}</p>
          <p className="text-muted">Silakan lakukan transaksi baru untuk paket ini.</p>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button color="secondary" onClick={() => setShowExpiredPopup(false)}>Tutup</Button>
          <Button color="warning" onClick={() => {
            setShowExpiredPopup(false);
          }}>Buat Transaksi Baru</Button>
        </ModalFooter>
      </Modal>

      <style>{`
        body { padding-top: 60px; }
        .custom-container-padding { padding-left: 1rem !important; padding-right: 1rem !important; }
        @media (min-width: 768px) { .custom-container-padding { padding-left: 1.5rem !important; padding-right: 1.5rem !important; } }
        @media (min-width: 1200px) { .custom-container-padding { padding-left: 2rem !important; padding-right: 2rem !important; } }
        .pricing-card-custom { border-radius: 1rem; overflow: hidden; transition: all 0.2s ease-in-out; border-width: 2px !important; }
        .pricing-card-custom:hover { transform: translateY(-5px); box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important; }
        .icon-feature-wrapper-active { width: 1.5rem; height: 1.5rem; border-radius: 50%; background-color: var(--bs-green); display: flex; align-items: center; justify-content: center; box-shadow: 0 0 5px rgba(0, 128, 0, 0.4); }
        .icon-feature-wrapper-active .icon-feature-check { width: 1rem; height: 1rem; color: white; stroke-width: 2.5; }
        .icon-feature-wrapper-inactive { width: 1.5rem; height: 1.5rem; display: flex; align-items: center; justify-content: center; }
        .icon-feature-wrapper-inactive .icon-feature-xmark { width: 1.25rem; height: 1.25rem; color: var(--bs-gray-500); stroke-width: 2; }
        .pricing-features-list li { font-size: 0.95rem; }
        .skeleton-loader-container { width: 100%; }
        .skeleton-line { background-color: #e0e0e0; border-radius: 4px; animation: pulse 1.5s infinite ease-in-out; }
        .skeleton-title { height: 1.25rem; width: 70%; margin-bottom: 0.75rem; }
        .skeleton-price { height: 1.5rem; width: 50%; margin-bottom: 0.5rem; }
        .skeleton-text { height: 1rem; width: 40%; margin-bottom: 1rem; }
        .skeleton-feature { height: 0.8rem; width: 90%; margin-bottom: 0.5rem; }
        @keyframes pulse {
          0% { background-color: #e0e0e0; }
          50% { background-color: #f0f0f0; }
          100% { background-color: #e0e0e0; }
        }
      `}</style>
    </div>
  );
};

export default PaketLangganan;